import React, { useState, useEffect } from 'react';
import {
  ArrowForwardIcon,
  BusinessIcon,
  Button,
  CheckCircleOutlineIcon,
  Chip,
  Dialog,
  DialogContent,
  DialogFooter,
  HighlightOffIcon,
  Input,
  PublicIcon,
  RemoveCircleOutlineIcon,
  Text,
} from '@billda/design-system';

export type OverrideState = 'enabled' | 'disabled' | 'default';

export type FeatureToggleAction =
  | { type: 'set'; scope: 'global'; currentState: OverrideState }
  | { type: 'remove'; scope: 'global'; currentState: OverrideState }
  | {
      type: 'set';
      scope: 'org';
      orgName: string;
      currentState: OverrideState;
    }
  | {
      type: 'remove';
      scope: 'org';
      orgName: string;
      currentState: OverrideState;
      fallbackState: OverrideState;
      fallbackSource: string;
    };

export interface FeatureToggleConfirmDialogProps {
  open: boolean;
  featureKey: string;
  featureDescription: string;
  action: FeatureToggleAction;
  isLoading: boolean;
  onConfirm: (isEnabled: boolean) => void;
  onCancel: () => void;
}

const stateLabels: Record<OverrideState, string> = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  default: 'Off (default)',
};

function StateChip({ state }: { state: OverrideState }) {
  if (state === 'default') {
    return (
      <Chip
        label={stateLabels[state]}
        size="sm"
        variant="outlined"
        className="h-6 text-[0.75rem] font-semibold text-content-secondary"
      />
    );
  }
  return (
    <Chip
      label={stateLabels[state]}
      color={state === 'enabled' ? 'success' : 'error'}
      size="sm"
      className="h-6 text-[0.75rem] font-semibold"
    />
  );
}

const FeatureToggleConfirmDialog: React.FC<FeatureToggleConfirmDialogProps> = ({
  open,
  featureKey,
  featureDescription,
  action,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [selectedState, setSelectedState] = useState<boolean>(true);

  const isSetAction = action.type === 'set';
  const skipConfirmInput = import.meta.env.DEV;
  const isConfirmTextValid = skipConfirmInput || confirmText === featureKey;
  const isGlobal = action.scope === 'global';

  // Default the toggle to the opposite of current effective state
  useEffect(() => {
    if (open && isSetAction) {
      setSelectedState(action.currentState !== 'enabled');
    }
  }, [open, isSetAction, action.currentState]);

  // Reset transient state once the dialog has closed
  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setSelectedState(true);
    }
  }, [open]);

  // New state chip for "remove" actions
  const removeNewState =
    action.type === 'remove' && 'fallbackState' in action
      ? action.fallbackState
      : 'default';
  const removeNewSource =
    action.type === 'remove' && 'fallbackSource' in action
      ? action.fallbackSource
      : undefined;

  const handleConfirm = () => {
    if (!isConfirmTextValid) return;
    onConfirm(isSetAction ? selectedState : false);
  };

  const handleClose = () => {
    if (isLoading) return;
    onCancel();
  };

  const scopeToken = isGlobal
    ? 'var(--color-content-warning)'
    : 'var(--color-content-info)';

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className="max-w-lg overflow-hidden rounded-3xl p-0"
        data-testid="feature-toggle-confirm-dialog"
      >
        {/* Header with scope accent */}
        <div
          className="border-b border-border-default"
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, ${scopeToken} 8%, transparent) 0%, transparent 60%)`,
          }}
        >
          <div className="flex items-center gap-3 px-5 pt-5 pb-3">
            <div
              className="flex size-9 items-center justify-center rounded-xl"
              style={{
                backgroundColor: `color-mix(in srgb, ${scopeToken} 15%, transparent)`,
                color: scopeToken,
              }}
            >
              {isGlobal ? (
                <PublicIcon aria-hidden="true" className="size-4" />
              ) : (
                <BusinessIcon aria-hidden="true" className="size-4" />
              )}
            </div>
            <div className="pr-8">
              <Text as="div" variant="body1" className="font-bold">
                {isSetAction ? 'Set Override' : 'Remove Override'}
              </Text>
              <Text variant="caption" color="secondary">
                {isGlobal
                  ? 'Affects all organisations'
                  : `${'orgName' in action ? action.orgName : ''} only`}
              </Text>
            </div>
          </div>
        </div>

        <div className="px-5 pt-6">
          {/* Feature identity */}
          <div className="mb-5">
            <Text
              as="div"
              variant="body2"
              className="mb-1 font-mono text-[0.85rem] font-bold tracking-tight text-content-primary"
            >
              {featureKey}
            </Text>
            {featureDescription && (
              <Text variant="caption" color="secondary">
                {featureDescription}
              </Text>
            )}
          </div>

          {/* State transition */}
          <div className="mb-5 rounded-xl border border-border-default bg-surface-canvas p-4">
            <Text
              as="div"
              variant="caption"
              color="secondary"
              className="mb-3 block font-semibold tracking-[0.5px] uppercase"
            >
              State Change
            </Text>
            <div className="flex flex-wrap items-center gap-3">
              <StateChip state={action.currentState} />
              <ArrowForwardIcon
                aria-hidden="true"
                className="size-4 text-content-disabled"
              />
              {isSetAction ? (
                <StateChip state={selectedState ? 'enabled' : 'disabled'} />
              ) : (
                <StateChip state={removeNewState} />
              )}
            </div>
            {!isSetAction && removeNewSource && (
              <Text
                as="div"
                variant="caption"
                color="secondary"
                className="mt-3 block italic"
              >
                Falls back to {removeNewSource}
              </Text>
            )}
          </div>

          {/* Override state selector */}
          {isSetAction && (
            <div className="mb-5">
              <Text
                as="div"
                variant="caption"
                color="secondary"
                className="mb-2 block font-semibold tracking-[0.5px] uppercase"
              >
                Override State
              </Text>
              <div
                className="flex w-full gap-2"
                data-testid="override-state-toggle"
              >
                <Button
                  type="button"
                  variant={selectedState ? 'primary' : 'outline'}
                  className="py-2 font-semibold"
                  onClick={() => setSelectedState(true)}
                >
                  <CheckCircleOutlineIcon
                    aria-hidden="true"
                    className="size-[18px]"
                  />
                  Enabled
                </Button>
                <Button
                  type="button"
                  variant={selectedState ? 'outline' : 'danger'}
                  className="py-2 font-semibold"
                  onClick={() => setSelectedState(false)}
                >
                  <HighlightOffIcon
                    aria-hidden="true"
                    className="size-[18px]"
                  />
                  Disabled
                </Button>
              </div>
            </div>
          )}

          {/* Type-to-confirm */}
          {!skipConfirmInput && (
            <div
              className={`rounded-xl border p-4 transition-colors duration-200 ${
                isConfirmTextValid
                  ? 'border-surface-success'
                  : 'border-border-default'
              }`}
            >
              <Text as="div" variant="body2" color="secondary" className="mb-2">
                Type{' '}
                <span className="rounded-md bg-surface-raised px-1.5 py-0.5 font-mono text-[0.8rem] font-bold text-content-primary">
                  {featureKey}
                </span>{' '}
                to confirm
              </Text>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={featureKey}
                autoComplete="off"
                className="font-mono text-[0.85rem]"
                data-testid="confirm-text-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="px-5 pt-2 pb-5">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isLoading}
            className="font-semibold"
          >
            Cancel
          </Button>
          <Button
            variant="neutral"
            onClick={handleConfirm}
            disabled={!isConfirmTextValid || isLoading}
            data-testid="feature-toggle-confirm-button"
            className="px-6 font-semibold"
          >
            {!isLoading &&
              (isSetAction ? (
                selectedState ? (
                  <CheckCircleOutlineIcon
                    aria-hidden="true"
                    className="size-4"
                  />
                ) : (
                  <HighlightOffIcon aria-hidden="true" className="size-4" />
                )
              ) : (
                <RemoveCircleOutlineIcon
                  aria-hidden="true"
                  className="size-4"
                />
              ))}
            {isLoading
              ? 'Applying...'
              : isSetAction
                ? selectedState
                  ? 'Enable'
                  : 'Disable'
                : 'Remove Override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeatureToggleConfirmDialog;
